import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CheckCircle2, Heart, Instagram, Facebook, Copy } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.02h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.11.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.36c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.83 2.41a8.19 8.19 0 0 1 2.41 5.83c0 4.54-3.7 8.24-8.24 8.24Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.16.25-.64.81-.79.97-.14.16-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.48-.41-.42-.56-.43h-.48c-.16 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.16 1.75 2.67 4.25 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29Z" />
  </svg>
);

const SignupContext = createContext(null);

export const useSignup = () => useContext(SignupContext);

export const SignupProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("waitlist");

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      const ch = params.get("ch");
      if (ref) localStorage.setItem("ss_ref", ref);
      if (ch) localStorage.setItem("ss_ch", ch);
    } catch {
      /* ignore */
    }
  }, []);

  const openSignup = (t = "waitlist") => {
    setType(t);
    setOpen(true);
  };

  return (
    <SignupContext.Provider value={{ openSignup }}>
      {children}
      <SignupDialog open={open} setOpen={setOpen} type={type} setType={setType} />
    </SignupContext.Provider>
  );
};

const SignupDialog = ({ open, setOpen, type, setType }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [myCode, setMyCode] = useState("");
  const [inviteCount, setInviteCount] = useState(null);

  const isBeta = type === "beta";

  const reset = () => {
    setName("");
    setEmail("");
    setDone(false);
    setAlreadyRegistered(false);
    setLoading(false);
    setMyCode("");
    setInviteCount(null);
  };

  const close = () => {
    setOpen(false);
    setTimeout(reset, 250);
  };

  const baseUrl =
    typeof window !== "undefined" ? window.location.origin : "https://sistersafe.app";
  const refLink = (channel) =>
    myCode ? `${baseUrl}/?ref=${myCode}&ch=${channel}` : baseUrl;
  const shareText =
    "I just joined SisterSafe, a women-first personal safety network. Join me!";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(refLink("copy"));
      toast.success("Referral link copied to clipboard");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const shareInstagram = async () => {
    const url = refLink("instagram");
    if (navigator.share) {
      try {
        await navigator.share({ title: "SisterSafe", text: shareText, url });
        return;
      } catch {
        /* user cancelled or unsupported, fall through */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied, paste it in your Instagram story or bio");
    } catch {
      toast.info("Copy the link and share it on Instagram");
    }
    window.open("https://instagram.com", "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (!myCode) return;
    let active = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/referral/${myCode}/stats`);
        if (active) setInviteCount(res.data?.count ?? 0);
      } catch {
        if (active) setInviteCount(0);
      }
    };
    load();
    const id = setInterval(load, 15000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [myCode]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/signup`, {
        name: name.trim(),
        email: email.trim(),
        signup_type: type,
        referred_by: localStorage.getItem("ss_ref") || null,
        referral_channel: localStorage.getItem("ss_ch") || null,
      });
      setMyCode(res.data?.referral_code || "");
      setDone(true);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        setAlreadyRegistered(true);
        setDone(true);
      } else {
        toast.error(detail || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="signup-dialog-overlay"
        >
          <div
            className="absolute inset-0 bg-[#111E16]/40 backdrop-blur-sm"
            onClick={close}
          />
          <motion.div
            className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl border border-[#E8EBE9] overflow-hidden"
            initial={{ y: 28, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
          >
            <button
              onClick={close}
              className="absolute right-5 top-5 text-[#8AA583] hover:text-[#2F5E43] transition-colors z-10"
              data-testid="signup-dialog-close"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            {done ? (
              <div className="p-10 text-center" data-testid="signup-success">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#E4EDE6]">
                  <CheckCircle2 className="h-8 w-8 text-[#2F5E43]" />
                </div>
                {alreadyRegistered ? (
                  <>
                    <h3 className="font-heading text-2xl font-semibold text-[#111E16]">
                      This email is already registered.
                    </h3>
                    <p className="mt-3 text-[#4A5F52] leading-relaxed">
                      <span className="font-semibold text-[#2F5E43]">{email}</span> is
                      already on our list. We'll notify you as soon as your{" "}
                      {isBeta ? "beta invitation" : "spot"} is ready.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="font-heading text-2xl font-semibold text-[#111E16]">
                      Welcome, {name.split(" ")[0]}!
                    </h3>
                    <p className="mt-3 text-[#4A5F52] leading-relaxed">
                      We've sent a confirmation email to{" "}
                      <span className="font-semibold text-[#2F5E43]">{email}</span>. We'll
                      notify you as soon as your {isBeta ? "beta invitation" : "spot"} is
                      ready.
                    </p>
                  </>
                )}

                <div className="mt-7 border-t border-[#EEF1EE] pt-6" data-testid="signup-share">
                  <p className="text-sm font-medium text-[#111E16]">
                    Help a sister stay safe, spread the word
                  </p>
                  {inviteCount !== null && (
                    <p
                      className="mt-1 text-sm text-[#4A5F52]"
                      data-testid="referral-counter"
                    >
                      <span className="font-semibold text-[#2F5E43]">{inviteCount}</span>{" "}
                      {inviteCount === 1 ? "friend" : "friends"} invited so far
                    </p>
                  )}
                  <div className="mt-3 flex items-center justify-center gap-3">
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${refLink("whatsapp")}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Share on WhatsApp"
                      className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E8EBE9] bg-white text-[#2F5E43] transition-colors hover:bg-[#2F5E43] hover:text-white"
                      data-testid="share-whatsapp"
                    >
                      <WhatsAppIcon className="h-5 w-5" />
                    </a>
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(refLink("facebook"))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Share on Facebook"
                      className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E8EBE9] bg-white text-[#2F5E43] transition-colors hover:bg-[#2F5E43] hover:text-white"
                      data-testid="share-facebook"
                    >
                      <Facebook className="h-5 w-5" />
                    </a>
                    <button
                      type="button"
                      onClick={shareInstagram}
                      aria-label="Share on Instagram"
                      className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E8EBE9] bg-white text-[#2F5E43] transition-colors hover:bg-[#2F5E43] hover:text-white"
                      data-testid="share-instagram"
                    >
                      <Instagram className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={copyLink}
                      aria-label="Copy link"
                      className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E8EBE9] bg-white text-[#2F5E43] transition-colors hover:bg-[#2F5E43] hover:text-white"
                      data-testid="share-copy-link"
                    >
                      <Copy className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <button
                  onClick={close}
                  className="mt-6 inline-flex items-center justify-center rounded-full bg-[#2F5E43] px-7 py-3 font-medium text-white transition-colors hover:bg-[#244A34]"
                  data-testid="signup-success-close"
                >
                  Done
                </button>
              </div>
            ) : (
              <div className="p-8 sm:p-10">
                <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#8AA583]">
                  <Heart className="h-3.5 w-3.5" /> SisterSafe
                </div>
                <h3 className="font-heading text-2xl font-semibold text-[#111E16]">
                  {isBeta ? "Become a Beta Tester" : "Join the Waitlist"}
                </h3>
                <p className="mt-2 text-sm text-[#4A5F52] leading-relaxed">
                  {isBeta
                    ? "Help shape a safety network built around women. Get early access and a direct line to our team."
                    : "Be the first to know when SisterSafe launches!"}
                </p>

                <div className="mt-5 inline-flex rounded-full bg-[#F2F5F2] p-1 text-sm">
                  <button
                    type="button"
                    onClick={() => setType("waitlist")}
                    className={`rounded-full px-4 py-1.5 font-medium transition-colors ${
                      !isBeta ? "bg-white text-[#2F5E43] shadow-sm" : "text-[#4A5F52]"
                    }`}
                    data-testid="toggle-waitlist"
                  >
                    Waitlist
                  </button>
                  <button
                    type="button"
                    onClick={() => setType("beta")}
                    className={`rounded-full px-4 py-1.5 font-medium transition-colors ${
                      isBeta ? "bg-white text-[#2F5E43] shadow-sm" : "text-[#4A5F52]"
                    }`}
                    data-testid="toggle-beta"
                  >
                    Beta Tester
                  </button>
                </div>

                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-[#111E16]">
                      Full name
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      placeholder="Jane Doe"
                      className="w-full rounded-xl border border-[#E8EBE9] bg-white px-4 py-3 text-[#111E16] outline-none transition-shadow focus:ring-2 focus:ring-[#2F5E43]/30"
                      data-testid="signup-name-input"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-[#111E16]">
                      Email address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="you@example.com"
                      className="w-full rounded-xl border border-[#E8EBE9] bg-white px-4 py-3 text-[#111E16] outline-none transition-shadow focus:ring-2 focus:ring-[#2F5E43]/30"
                      data-testid="signup-email-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#2F5E43] px-7 py-3.5 font-medium text-white transition-colors hover:bg-[#244A34] disabled:opacity-70"
                    data-testid="signup-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                      </>
                    ) : isBeta ? (
                      "Request Beta Access"
                    ) : (
                      "Join the Waitlist"
                    )}
                  </button>
                  <p className="text-center text-xs text-[#8AA583]">
                    We respect your privacy. No spam, ever.
                  </p>
                </form>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
